import SUMSEL_BABEL_KONVEN from "../../assets/images/icon_topup/logo_bsb_konven.png";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TOPUP_FEATURE_STORAGE_KEY = "psp-mobile-feature";

const BANK_KEYWORDS = {
  MASKED_PREFIX: "XENDIT",
  SUMSELBABEL: "SUMSELBABEL",
} as const;

const DISPLAY_NAME = {
  MASKED: "BANK LAIN",
  SUMSELBABEL: "Bank Sumsel Babel",
} as const;

/** BNI variants that should be prioritized when smartschool feature is enabled */
const BNI_PRIORITY_VARIANTS = new Set<string>(["BNI", "BNI-SNAP", "BNI SNAP"]);

const MASKED_BANK_ICON_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" width="160" height="48" viewBox="0 0 160 48" fill="none">
  <rect width="160" height="48" rx="12" fill="#E5E7EB"/>
  <rect x="18" y="12" width="28" height="24" rx="6" fill="#9CA3AF"/>
  <rect x="58" y="16" width="76" height="6" rx="3" fill="#9CA3AF"/>
  <rect x="58" y="26" width="56" height="6" rx="3" fill="#C4C4C4"/>
</svg>`.trim();

const MASKED_BANK_ICON = `data:image/svg+xml;utf8,${encodeURIComponent(MASKED_BANK_ICON_SVG)}`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TopupFeatureModules = {
  is_smartschool?: boolean;
  is_banksumsel_konven?: boolean;
};

type TopupFeatureConfig = {
  modules?: TopupFeatureModules;
};

type BankItem = {
  bankName?: string;
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const isBniPriorityBank = (bankName?: string): boolean =>
  Boolean(bankName && BNI_PRIORITY_VARIANTS.has(bankName));

// ---------------------------------------------------------------------------
// Feature config
// ---------------------------------------------------------------------------

export const getTopupFeatureConfig = (): TopupFeatureConfig => {
  try {
    const raw = localStorage.getItem(TOPUP_FEATURE_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as TopupFeatureConfig) : {};
  } catch {
    return {};
  }
};

export const isSmartschoolTopupEnabled = (): boolean =>
  Boolean(getTopupFeatureConfig().modules?.is_smartschool);

export const isSumselBabelKonvenEnabled = (): boolean =>
  Boolean(getTopupFeatureConfig().modules?.is_banksumsel_konven);

// ---------------------------------------------------------------------------
// Bank predicates
// ---------------------------------------------------------------------------

export const isMaskedBank = (bankName = ""): boolean =>
  isSmartschoolTopupEnabled() &&
  bankName.startsWith(BANK_KEYWORDS.MASKED_PREFIX);

export const isBankSumselKonven = (bankName = ""): boolean =>
  isSumselBabelKonvenEnabled() && bankName.includes(BANK_KEYWORDS.SUMSELBABEL);

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export const getMaskedBankIcon = (): string => MASKED_BANK_ICON;

export const getBankDisplayName = (bankName = "", defaultName = ""): string => {
  if (isMaskedBank(bankName)) return DISPLAY_NAME.MASKED;
  if (isBankSumselKonven(bankName)) return DISPLAY_NAME.SUMSELBABEL;
  return defaultName;
};

export const getBankDisplayIcon = (bankName = "", defaultIcon = ""): string => {
  if (isMaskedBank(bankName)) return getMaskedBankIcon();
  if (isBankSumselKonven(bankName)) return SUMSEL_BABEL_KONVEN;
  return defaultIcon;
};

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

export const sortTopupBanks = <T extends BankItem>(banks: T[]): T[] => {
  if (!isSmartschoolTopupEnabled()) return banks;

  return [...banks].sort((left, right) => {
    const leftIsBni = isBniPriorityBank(left?.bankName);
    const rightIsBni = isBniPriorityBank(right?.bankName);

    if (leftIsBni === rightIsBni) return 0;
    return leftIsBni ? -1 : 1;
  });
};
